import {
  Children,
  cloneElement,
  ComponentType,
  createContext,
  isValidElement,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs';
import { AnimatePresence, motion } from 'motion/react';

import { cn } from '@components/primitives';
import { Check } from '@components/primitives/AnimatedIcon/Check';
import { focusWithin } from '@components/primitives/focus';

import css from './styles.module.css';

interface StepperContextType {
  value: number | string;
  complete: boolean;
  canChange: boolean;
}

const StepperContext = createContext<StepperContextType>({
  value: '',
  complete: false,
  canChange: false,
});

export interface StepperItemProps extends PropsWithChildren {
  value: number | string;
  state: 'active' | 'complete' | 'idle';
}

export function StepperItem({ state, value, children }: StepperItemProps) {
  const { canChange } = useContext(StepperContext);
  return (
    <li
      className={css.stepperItem}
      data-state={state}
      data-can-change={canChange}
    >
      <TabsTrigger
        value={String(value)}
        disabled={!canChange || state !== 'complete'}
      >
        <AnimatePresence initial={false}>
          {state === 'complete' && (
            <motion.div
              className={css.stepperItemCheck}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{
                opacity: 0,
                scale: 0.9,
                transition: { duration: 0.1, ease: 'easeOut' },
              }}
              transition={{
                duration: 0.2,
                ease: 'easeOut',
                scale: {
                  type: 'spring',
                  bounce: 0.35,
                  visualDuration: 0.2,
                },
              }}
            >
              <Check
                aria-hidden
                initial="from"
                animate="to"
                exit="to"
                transition={{ delay: 0.02, duration: 0.2, ease: 'easeOut' }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {children ?? value}
      </TabsTrigger>
    </li>
  );
}

export interface StepperListProps extends PropsWithChildren {
  className?: string;
  'aria-label'?: string;
}

export function StepperList({
  children,
  className,
  'aria-label': ariaLabel,
}: StepperListProps) {
  const { value, complete } = useContext(StepperContext);
  const currentIndex = Children.toArray(children).findIndex(
    (child) => isValidElement(child) && child.props.value === value
  );

  return (
    <TabsList
      asChild
      aria-label={ariaLabel}
      tabIndex={-1}
      onClick={(event) => event.preventDefault()}
    >
      <ol className={cn(css.stepper, className)}>
        {Children.map(children, (child, index) => {
          if (isValidElement(child)) {
            let state = 'idle';
            if (currentIndex === index) state = 'active';
            if (currentIndex > index || complete) state = 'complete';

            return cloneElement(child, { state } as any);
          } else {
            return child;
          }
        })}
      </ol>
    </TabsList>
  );
}

export interface StepperContentProps extends PropsWithChildren {
  className?: string;
  value: string | number;
}

export function StepperContent({
  children,
  className,
  value,
}: StepperContentProps) {
  return (
    <TabsContent value={value.toString()} asChild tabIndex={-1}>
      <div
        className={cn(css.content, className)}
        data-content-id={value.toString()}
      >
        {children}
      </div>
    </TabsContent>
  );
}

export interface StepperRootProps extends PropsWithChildren {
  className?: string;
  value: string | number;
  onValueChange?: (value: string) => void;
  complete?: boolean;
  /**
   * Called when the content is auto-focused.
   * Use `event.preventDefault()` to prevent the auto-focus.
   */
  onAutoFocus?: (event: Event) => void;
}

export function StepperRoot({
  children,
  className,
  value,
  onValueChange,
  onAutoFocus,
  complete = false,
}: StepperRootProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  const canChange = !!onValueChange;
  const context = useMemo(
    () => ({ value, complete, canChange }),
    [value, complete, canChange]
  );

  const didMount = useRef(false);
  // Cleanup didMount on fast refresh
  useEffect(
    () => () => {
      didMount.current = false;
    },
    []
  );

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    requestAnimationFrame(() => {
      const event = new Event('focus', { cancelable: true });
      onAutoFocus?.(event);
      if (event.defaultPrevented) return;

      const query = `[data-content-id="${value}"]`;
      const element = rootRef.current?.querySelector(query);
      if (element) {
        focusWithin(element);
      }
    });
  }, [value]);

  return (
    <StepperContext.Provider value={context}>
      <Tabs
        ref={rootRef}
        className={cn(css.root, className)}
        value={value.toString()}
        onValueChange={onValueChange}
        activationMode="manual"
      >
        {children}
      </Tabs>
    </StepperContext.Provider>
  );
}

export const Stepper = Object.assign(StepperRoot, {
  List: StepperList,
  Item: StepperItem as ComponentType<Omit<StepperItemProps, 'state'>>,
  Content: StepperContent,
});
